import { Route, Routes, Navigate } from 'react-router-dom'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { withTranslation } from 'react-i18next'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import annotationPlugin from 'chartjs-plugin-annotation'
import { Font } from '@react-pdf/renderer'
import Chart from 'chart.js/auto'
import { persistor } from './store'
import { Store } from './store/store'
import FsDrawer from './components/fs-drawer/fs-drawer'
import FsSignIn from './components/login-form/login-form'
import FsForgotPassword from './components/login-form/forgot-password'
import FsResetPassword from './components/login-form/reset-password'
import AuthorizedRoute from './AuthorizedRoute'
import FsRoutes from './FsRoutes'
import { FsRoute } from './shared/model'
import AdminRoute from './AdminRoute'
import 'react-dropzone-uploader/dist/styles.css'
import FsImportView from './views/fs-import-view/fs-import-view'
import PtImportView from './views/fs-import-view/pt-import-view'
import FsConfWizardView from './views/fs-configuration-view/fs-conf-wizard-view'
import FsEditReportView from './views/fs-report-view/fs-edit-report-view'
import FsOFAView from './views/fs-ofa-view/fs-ofa-view'
import FsTimelineView from './views/fs-timeline-view/fs-timeline-view'
import FsCausesView from './views/fs-causes-view/fs-causes.view'
import FsTheme from './components/fs-theme/fs-theme'
import RalewayFont from './fonts/RalewayMedium.ttf'
import RalewayBoldFont from './fonts/RalewayBold.ttf'
import './App.css'
import FsHome from './views/fs-home-view/fs-home-view'
import PtRoutes from './PtRoutes'
import HomeDrawer from './components/fs-drawer/home-drawer'
import PtDrawer from './components/fs-drawer/pt-drawer'

export const muiCache = createCache({
  key: 'mui',
  prepend: true,
})

Chart.register(ChartDataLabels)
Chart.register(annotationPlugin)
Chart.register({
  id: 'legendMargin',
  beforeInit: (chart) => {
    if (chart.legend) {
      // @ts-ignore
      const originalFit = chart.legend.fit

      // @ts-ignore
      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)()
        this.height += 50
      }
    }
  },
})
Chart.defaults.font.size = 14
Chart.defaults.font.lineHeight = 3
Chart.defaults.font.family = 'Raleway'
Chart.defaults.font.weight = 'normal'

declare module '@mui/material/styles' {
  interface Palette {
    fsPurple: Palette['primary']
    fsBlue: Palette['primary']
    fsDarkBlue: Palette['primary']
    fsMagenta: Palette['primary']
    fsLightGreen: Palette['primary']
    fsLightOrange: Palette['primary']
    fsGreen: Palette['primary']
    fsLightBlue: Palette['primary']
    fsOrange: Palette['primary']
    fsGray: Palette['primary']
  }
  interface PaletteOptions {
    fsPurple: PaletteOptions['primary']
    fsBlue: PaletteOptions['primary']
    fsDarkBlue: PaletteOptions['primary']
    fsMagenta: PaletteOptions['primary']
    fsLightGreen: PaletteOptions['primary']
    fsLightOrange: PaletteOptions['primary']
    fsGreen: PaletteOptions['primary']
    fsLightBlue: PaletteOptions['primary']
    fsOrange: PaletteOptions['primary']
    fsGray: PaletteOptions['primary']
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    fsPurple: true
    fsBlue: true
    fsDarkBlue: true
    fsMagenta: true
    fsLightGreen: true
    fsLightOrange: true
    fsGreen: true
    fsLightBlue: true
    fsOrange: true
    fsGray: true
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsColorOverrides {
    fsPurple: true
    fsBlue: true
    fsDarkBlue: true
    fsMagenta: true
    fsLightGreen: true
    fsLightOrange: true
    fsGreen: true
    fsLightBlue: true
    fsOrange: true
    fsGray: true
  }
}

declare module '@mui/material' {
  interface InputBasePropsColorOverrides {
    fsPurple: true
    fsBlue: true
    fsDarkBlue: true
    fsMagenta: true
    fsLightGreen: true
    fsLightOrange: true
    fsGreen: true
    fsLightBlue: true
    fsOrange: true
    fsGray: true
  }
}
declare module '@mui/material' {
  interface FormLabelPropsColorOverrides {
    fsPurple: true
    fsBlue: true
    fsDarkBlue: true
    fsMagenta: true
    fsLightGreen: true
    fsLightOrange: true
    fsGreen: true
    fsLightBlue: true
    fsOrange: true
    fsGray: true
  }
}

Font.registerHyphenationCallback((word) => [word])

Font.register({
  family: 'Raleway',
  fonts: [
    {
      src: RalewayFont,
      fontStyle: 'normal',
      fontWeight: 400,
    },
  ],
})

Font.register({
  family: 'RalewayBold',
  fonts: [
    {
      src: RalewayBoldFont,
      fontStyle: 'normal',
      fontWeight: 400,
    },
  ],
})

function App() {
  return (
    <CacheProvider value={muiCache}>
      <Provider store={Store}>
        <PersistGate persistor={persistor} loading={null}>
          <FsTheme>
            <Routes>
              <Route path="/" element={<Navigate replace to="/ines/home" />} />
              <Route path="/login" element={<FsSignIn />} />
              <Route path="/forgot-password" element={<FsForgotPassword />} />
              <Route path="/reset-password" element={<FsResetPassword />} />
              <Route path="/" element={<AuthorizedRoute />}>
                <Route path="ines" element={<HomeDrawer />}>
                  <Route path="/ines/home" element={<FsHome />} key="route-nested-ines-home" />
                </Route>
                <Route path="fs" element={<FsDrawer />}>
                  <Route path="admin" element={<AdminRoute />}>
                    <Route
                      path="/fs/admin/import"
                      element={<FsImportView />}
                      key="route-nested-import-view"
                    />
                    <Route
                      path="/fs/admin/config"
                      element={<FsConfWizardView />}
                      key="route-nested-config-view"
                    />
                  </Route>
                  {FsRoutes.map((route: FsRoute) => (
                    <Route
                      index={route.index}
                      path={route.path}
                      element={route.component}
                      key={`route-nested-${route.name}`}
                    />
                  ))}
                  <Route
                    path="/fs/ofa/simulator/:id"
                    element={<FsOFAView />}
                    key="route-nested-ofa-simulator-view"
                  />
                  <Route
                    path="/fs/timeline/:id"
                    element={<FsTimelineView />}
                    key="route-nested-timeline-view"
                  />
                  <Route
                    path="/fs/causes/:id"
                    element={<FsCausesView />}
                    key="route-nested-causes-view"
                  />
                </Route>
                <Route path="fs" element={<FsDrawer fullScreen />}>
                  <Route
                    path="/fs/report/edit/:id"
                    element={<FsEditReportView />}
                    key="route-nested-report-edit-view"
                  />
                </Route>
                <Route path="pt" element={<PtDrawer />}>
                  <Route path="admin" element={<AdminRoute />}>
                    <Route
                      path="/pt/admin/import"
                      element={<PtImportView />}
                      key="route-nested-pt-import-view"
                    />
                  </Route>
                  {PtRoutes.map((route: FsRoute) => (
                    <Route
                      index={route.index}
                      path={route.path}
                      element={route.component}
                      key={`route-nested-${route.name}`}
                    />
                  ))}
                </Route>
              </Route>
            </Routes>
          </FsTheme>
        </PersistGate>
      </Provider>
    </CacheProvider>
  )
}

export default withTranslation()(App)
